import React, { useEffect, useMemo, useState } from "react";
import countries from "i18n-iso-countries";
import { useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { parseHtml } from "@components/HTMLParser/utils";
import { FormAutocomplete } from "@components/inputs/form/FormAutocomplete/FormAutocomplete";
import { FormCheckboxGroup } from "@components/inputs/form/FormCheckboxGroup/FormCheckboxGroup";
import { FormDatepicker } from "@components/inputs/form/FormDatepicker/FormDatepicker";
import { FormInput } from "@components/inputs/form/FormInput/FormInput";
import { Radio } from "@components/inputs/Radio/Radio";
import { Notification } from "@components/Notification/Notification";
import { useApiErrorUtils } from "@hooks/useApiErrorUtils";
import { useSubmitPersonalInformationMutation } from "@services/knowYourCustomer/mutations";
import { usePersonalInformationQuery } from "@services/knowYourCustomer/queries";
import type {
  IPersonalInformation,
  KycFormStep,
  TKnowYourCustomerStepAction,
} from "@services/knowYourCustomer/types";
import { isNullOrUndefined, useCurrentLanguageCode } from "@utils/common";
import { COUNTRY_CODE } from "@/constants";
import { KYCModalActionButtons } from "../KYCModalActionButtons/KYCModalActionButtons";

interface IPersonalInformationFormProps {
  onChangeStep: (step: KycFormStep) => void;
}

export const PersonalInformationForm: React.FC<
  IPersonalInformationFormProps
> = ({ onChangeStep }) => {
  const { t } = useTranslation();
  const currentLanguageCode = useCurrentLanguageCode();
  const { data: personalInformation } = usePersonalInformationQuery();

  const { mutateAsync: submitPersonalInformation, isPending } =
    useSubmitPersonalInformationMutation();

  const residencePermitOptions = [
    {
      label: t("KYC.modal.step1.form.fields.residencePermit.options.yes"),
      value: true,
    },
    {
      label: t("KYC.modal.step1.form.fields.residencePermit.options.no"),
      value: false,
    },
  ];

  const [isResidentOfThisCountry, setIsResidentOfThisCountry] =
    useState<boolean>(true);

  const { paymentPurposeOptions, citizenshipOptions } = useMemo(() => {
    if (!personalInformation) {
      return { paymentPurposeOptions: [], citizenshipOptions: [] };
    }

    const paymentPurposeOptions =
      personalInformation.paymentPurposeTypes?.map((item) => ({
        value: item.paymentPurposeType,
        label: t(
          `KYC.modal.step1.form.fields.selectedPaymentPurposeTypes.options.${item.paymentPurposeType}`,
        ),
      })) ?? [];

    let citizenshipOptions = (personalInformation.citizenships ?? [])
      .map((citizenship) => {
        const countryName =
          countries.getName(citizenship.countryCode, currentLanguageCode) ??
          citizenship.name;

        return {
          value: citizenship.citizenshipId,
          label: countryName,
        };
      })
      .sort((a, b) => a.label.localeCompare(b.label));

    // move current country to top of the list
    const currentCountry = (personalInformation.citizenships ?? []).find(
      (i) => i.countryCode === COUNTRY_CODE.toUpperCase(),
    );
    const currentCountryOptionIndex = (citizenshipOptions ?? []).findIndex(
      (i) => i.value === currentCountry?.citizenshipId,
    );
    const currentCountryOption = citizenshipOptions[currentCountryOptionIndex];
    if (currentCountryOption) {
      citizenshipOptions.splice(currentCountryOptionIndex, 1);
      citizenshipOptions = [currentCountryOption, ...citizenshipOptions];
    }

    return { paymentPurposeOptions, citizenshipOptions };
  }, [personalInformation, t, currentLanguageCode]);

  const {
    handleSubmit,
    control,
    setError,
    setValue,
    resetField,
    formState: { touchedFields },
  } = useForm<IPersonalInformation>({
    defaultValues: personalInformation,
  });

  const [
    citizenshipIdValue,
    secondCitizenshipIdValue,
    selectedPaymentPurposeTypeValues,
  ] = useWatch({
    name: [
      "citizenshipId",
      "secondCitizenshipId",
      "selectedPaymentPurposeTypes",
    ],
    control,
  });

  // Properly clear the field with no defaultValue
  useEffect(() => {
    if (isNullOrUndefined(secondCitizenshipIdValue)) {
      resetField("secondCitizenshipId", { defaultValue: null });
    }
  }, [resetField, secondCitizenshipIdValue]);

  const { setApiErrors } = useApiErrorUtils(setError);

  const submitPersonalInformationForm = async (
    data: IPersonalInformation,
    step?: TKnowYourCustomerStepAction,
  ) => {
    delete data.citizenships;
    delete data.paymentPurposeTypes;
    const {
      citizenshipId,
      secondCitizenshipId,
      dateOfBirth,
      personalCode,
      secondDateOfBirth,
      secondPersonalCode,
      ...rest
    } = data;

    const nextForm = await submitPersonalInformation({
      ...rest,
      ...{
        citizenshipId: citizenshipId ?? null,
        secondCitizenshipId: secondCitizenshipId ?? null,
        dateOfBirth: null,
        personalCode: null,
        secondDateOfBirth: null,
        secondPersonalCode: null,
      },
      ...(personalInformation?.citizenships?.find(
        (citizenship) => citizenship.citizenshipId === citizenshipId,
      )?.personalCodeExists
        ? { personalCode }
        : { dateOfBirth }),
      ...(personalInformation?.citizenships?.find(
        (citizenship) => citizenship.citizenshipId === secondCitizenshipId,
      )?.personalCodeExists
        ? { secondPersonalCode }
        : { secondDateOfBirth }),
      step,
    }).catch(setApiErrors);

    if (!isNullOrUndefined(nextForm)) {
      onChangeStep(nextForm);
    }
  };

  const resolveId = (
    formVal?: number | null,
    personalVal?: number | null,
    touched?: boolean,
  ) => {
    const hasFormVal = formVal !== null && formVal !== undefined;
    if (!hasFormVal && touched === false && personalVal != null)
      return personalVal;
    return formVal ?? personalVal ?? undefined;
  };

  const primaryCitizenshipId = resolveId(
    citizenshipIdValue,
    personalInformation?.citizenshipId,
  );

  const secondaryCitizenshipId = resolveId(
    secondCitizenshipIdValue,
    personalInformation?.secondCitizenshipId,
    touchedFields?.secondCitizenshipId,
  );

  const primaryCitizenshipInfo = useMemo(
    () =>
      primaryCitizenshipId != null
        ? personalInformation?.citizenships?.find(
            (c) => c.citizenshipId === primaryCitizenshipId,
          )
        : undefined,
    [primaryCitizenshipId, personalInformation?.citizenships],
  );

  const secondaryCitizenshipInfo = useMemo(
    () =>
      secondaryCitizenshipId != null
        ? personalInformation?.citizenships?.find(
            (c) => c.citizenshipId === secondaryCitizenshipId,
          )
        : undefined,
    [secondaryCitizenshipId, personalInformation?.citizenships],
  );

  const showResidencePermit =
    primaryCitizenshipInfo?.countryCode !== COUNTRY_CODE.toUpperCase() &&
    secondaryCitizenshipInfo?.countryCode !== COUNTRY_CODE.toUpperCase();

  const showPrimaryCitizenshipInformationField = primaryCitizenshipId != null;
  const showSecondaryCitizenshipInformationField =
    secondaryCitizenshipId != null;

  const primaryNeedsCode = !!primaryCitizenshipInfo?.personalCodeExists;
  const secondaryNeedsCode = !!secondaryCitizenshipInfo?.personalCodeExists;

  return (
    <div className="flex flex-col gap-6">
      <p className="text-gray-900 p-0 text-lg font-semibold">
        {t("KYC.modal.step1.title")}
      </p>
      <p>{t("KYC.modal.step1.description")}</p>
      <form
        onSubmit={handleSubmit((data) => submitPersonalInformationForm(data))}
        className="flex flex-col gap-3"
      >
        <div className="flex flex-col gap-4 md:flex-row md:gap-3">
          <FormInput
            name="firstName"
            control={control}
            label={t("KYC.modal.step1.form.fields.firstName.label")}
            placeholder={t("KYC.modal.step1.form.fields.firstName.placeholder")}
          />
          <FormInput
            name="lastName"
            control={control}
            label={t("KYC.modal.step1.form.fields.lastName.label")}
            placeholder={t("KYC.modal.step1.form.fields.lastName.placeholder")}
          />
        </div>
        <div className="flex flex-col gap-4 md:flex-row md:gap-3">
          <div className="flex w-full flex-col gap-3">
            <FormAutocomplete
              name="citizenshipId"
              options={citizenshipOptions}
              control={control}
              label={t("KYC.modal.step1.form.fields.citizenshipId.label")}
              placeholder={t(
                "KYC.modal.step1.form.fields.citizenshipId.placeholder",
              )}
              className="w-full"
            />
            {showPrimaryCitizenshipInformationField &&
              (primaryNeedsCode ? (
                <FormInput
                  name="personalCode"
                  control={control}
                  label={t("KYC.modal.step1.form.fields.personalCode.label")}
                  placeholder={t(
                    "KYC.modal.step1.form.fields.personalCode.placeholder",
                  )}
                />
              ) : (
                <FormDatepicker
                  name="dateOfBirth"
                  control={control}
                  label={t("KYC.modal.step1.form.fields.dateOfBirth.label")}
                  placeholder={t(
                    "KYC.modal.step1.form.fields.dateOfBirth.placeholder",
                  )}
                />
              ))}
          </div>
          <div className="flex w-full flex-col gap-3">
            <FormAutocomplete
              name="secondCitizenshipId"
              options={citizenshipOptions}
              control={control}
              placeholder={t(
                "KYC.modal.step1.form.fields.secondCitizenshipId.placeholder",
              )}
              label="&nbsp;"
              className="w-full"
              isClearEnabled
              onClear={() => {
                setValue("secondPersonalCode", null);
                setValue("secondDateOfBirth", null);
              }}
            />
            {showSecondaryCitizenshipInformationField &&
              (secondaryNeedsCode ? (
                <FormInput
                  name="secondPersonalCode"
                  control={control}
                  label={t(
                    "KYC.modal.step1.form.fields.secondPersonalCode.label",
                  )}
                  placeholder={t(
                    "KYC.modal.step1.form.fields.secondPersonalCode.placeholder",
                  )}
                />
              ) : (
                <FormDatepicker
                  name="secondDateOfBirth"
                  control={control}
                  label={t(
                    "KYC.modal.step1.form.fields.secondDateOfBirth.label",
                  )}
                  placeholder={t(
                    "KYC.modal.step1.form.fields.secondDateOfBirth.placeholder",
                  )}
                />
              ))}
          </div>
        </div>
        <Notification
          color="red"
          description={
            parseHtml(t("KYC.modal.step1.notification")) as JSX.Element
          }
          className="my-3"
        />
        {showResidencePermit && (
          <React.Fragment>
            <p className="text-gray-900 p-0 text-lg font-semibold">
              {t("KYC.modal.step1.form.fields.residencePermit.title")}
            </p>
            <Radio
              aria-description={t(
                "KYC.modal.step1.form.fields.residencePermit.title",
              )}
              defaultValue={isResidentOfThisCountry as unknown as string}
              onRadioOptionChange={(value) =>
                setIsResidentOfThisCountry(value as boolean)
              }
              className="grid grid-cols-2"
              options={residencePermitOptions}
              name="residencePermit"
            />
            <div className="grid grid-cols-2 gap-4">
              {isResidentOfThisCountry && (
                <FormInput
                  name="residencyPersonalCode"
                  control={control}
                  rules={{
                    required: {
                      value: showResidencePermit && isResidentOfThisCountry,
                      message: t("common.form.field.errors.required"),
                    },
                  }}
                  label={t(
                    "KYC.modal.step1.form.fields.residencyPersonalCode.label",
                  )}
                  placeholder={t(
                    "KYC.modal.step1.form.fields.residencyPersonalCode.placeholder",
                  )}
                />
              )}
            </div>
          </React.Fragment>
        )}
        <p className="text-gray-900 p-0 text-lg font-semibold">
          {t("KYC.modal.step1.form.fields.selectedPaymentPurposeTypes.title")}
        </p>
        <FormCheckboxGroup
          name="selectedPaymentPurposeTypes"
          options={paymentPurposeOptions}
          control={control}
        />
        <FormInput
          containerClassName={`${
            !personalInformation?.paymentPurposeTypes?.some(
              (type) =>
                selectedPaymentPurposeTypeValues?.includes(
                  type.paymentPurposeType,
                ) && type.shouldFillOut,
            ) && "invisible"
          }`}
          name="paymentPurposeDescription"
          control={control}
          placeholder={t(
            "KYC.modal.step1.form.fields.paymentPurposeDescription.placeholder",
          )}
        />
      </form>
      <KYCModalActionButtons
        onSubmit={handleSubmit((data) => submitPersonalInformationForm(data))}
        onCancel={handleSubmit((data) =>
          submitPersonalInformationForm(data, "close"),
        )}
        isLoading={isPending}
      />
    </div>
  );
};
